import { RefObject, useCallback, useEffect, useState } from 'react';

interface UseHeaderNotificationsMenuOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  onMenuOpened?: () => void | Promise<void>;
}

interface UseHeaderNotificationsMenuResult {
  isOpen: boolean;
  closeMenu: () => void;
  toggleMenu: () => Promise<void>;
}

const useHeaderNotificationsMenu = ({
  containerRef,
  onMenuOpened,
}: UseHeaderNotificationsMenuOptions): UseHeaderNotificationsMenuResult => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [containerRef, isOpen]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleMenu = useCallback(async () => {
    const nextOpenState = !isOpen;
    setIsOpen(nextOpenState);

    if (nextOpenState && onMenuOpened) {
      await onMenuOpened();
    }
  }, [isOpen, onMenuOpened]);

  return {
    isOpen,
    closeMenu,
    toggleMenu,
  };
};

export default useHeaderNotificationsMenu;
